import React, {useMemo} from 'react';
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import type {HotspotExplorerProps, HotspotExplorerSortMode} from "../types";
import {
    buildCountryPath,
    buildHotspotDetailPath,
    getHotspotExplorerStats,
    getHotspotExplorerHeroImage,
    getVisibleHotspots,
    sortHotspots
} from "../helpers";
import {HotspotExplorerShell} from "../hotspot-explorer-shell";

export function AllHotspotsView({hotspotExplorerIndex, lifeListLength, homeLocation}: HotspotExplorerProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const sortMode = (searchParams.get('sort') as HotspotExplorerSortMode) || 'visits';
    const hotspots = hotspotExplorerIndex.hotspots;
    const visibleHotspots = useMemo(() => sortHotspots(getVisibleHotspots(hotspots, {}), sortMode), [hotspots, sortMode]);
    const countryGroups = hotspotExplorerIndex.countryGroups;

    const setSortMode = (nextSortMode: HotspotExplorerSortMode) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('sort', nextSortMode);
        setSearchParams(nextParams, {replace: true});
    }

    const openCountry = (countryCode: string) => {
        navigate(`${buildCountryPath(countryCode)}${location.search}`);
    }

    const openHotspot = (locationId: string) => {
        navigate(buildHotspotDetailPath(locationId), {
            state: {returnTo: `${location.pathname}${location.search}`}
        });
    }

    const stats = useMemo(() => {
        return {
            ...getHotspotExplorerStats(visibleHotspots, 'Countries'),
            speciesSeen: lifeListLength
        };
    }, [lifeListLength, visibleHotspots]);

    return (
        <HotspotExplorerShell
            backgroundImage={getHotspotExplorerHeroImage({routeLevel: 'landing', homeLocation})}
            eyebrow="Hotspot Explorer"
            title="See Where You’ve Been"
            intro="Browse the hotspots you have visited and drill down by country and region."
            stats={stats}
            showYearsExploring={false}
            panelHeading="By Country"
            groups={countryGroups}
            onSelectGroup={openCountry}
            hotspots={visibleHotspots}
            sortMode={sortMode}
            onChangeSort={setSortMode}
            onOpenHotspot={openHotspot}
        />
    );
}
